import { Box } from '@mui/material';
import React, { useRef, useState } from 'react';
import ContentManagementInterface from '../../models/ContentManagementInterface';
import { StyledTypography } from '../styles/styles';
import ContentDiffDisplayPanel from './ContentDiffDisplayPanel';
import { ContentDiffComparisonProps } from './contentDiffComparisonTypes';
import { DIFF_MEASUREMENT_CONTAINER_STYLE } from './contentDiffComparisonUtils';
import { useDefaultRenderContentItem, useRenderContentReadOnly } from './contentDiffRenderUtils';
import { useCommentOverlayPositions } from './useCommentOverlayPositions';
import { useContentDiffCalculation } from './useContentDiffCalculation';

export type { ContentDiffComparisonProps } from './contentDiffComparisonTypes';

const ContentDiffComparison: React.FC<ContentDiffComparisonProps> = ({
  originalContent,
  newContent,
  tone = 'default',
  renderContentItem,
  containerSx,
  renderActions,
  sectionData = [],
  comments = [],
  currentUser,
  onReply,
  onAcceptChanges,
}) => {
  const [originalRef, setOriginalRef] = useState<HTMLDivElement | null>(null);
  const [newRef, setNewRef] = useState<HTMLDivElement | null>(null);
  const diffContainerRef = useRef<HTMLDivElement | null>(null);

  const isUserReady = !!currentUser;

  const defaultRenderContentItem = useDefaultRenderContentItem(tone);
  const renderContentReadOnly = useRenderContentReadOnly(
    renderContentItem,
    defaultRenderContentItem,
  );

  const { isCalculating, diffHtml, error } = useContentDiffCalculation({
    originalContent,
    newContent,
    originalRef,
    newRef,
    isUserReady,
  });

  const overlayPositions = useCommentOverlayPositions(comments, diffHtml, diffContainerRef);

  if (!originalContent || !newContent) {
    console.error('ContentDiffComparison: Missing required props', {
      originalContent: !!originalContent,
      newContent: !!newContent,
    });
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <StyledTypography color="error">Missing content data for comparison</StyledTypography>
      </Box>
    );
  }

  return (
    <>
      {isUserReady && (
        <>
          <div
            ref={setOriginalRef}
            style={DIFF_MEASUREMENT_CONTAINER_STYLE}
            data-diff-key="original-content"
            data-diff-mode="words"
            className="original-content"
          >
            {renderContentReadOnly(originalContent, true, newContent, false)}
          </div>
          <div
            ref={setNewRef}
            style={DIFF_MEASUREMENT_CONTAINER_STYLE}
            data-diff-key="new-content"
            data-diff-mode="words"
            className="new-content"
          >
            {renderContentReadOnly(newContent, false, originalContent, true)}
          </div>
        </>
      )}

      <ContentDiffDisplayPanel
        isUserReady={isUserReady}
        isCalculating={isCalculating}
        error={error}
        diffHtml={diffHtml}
        diffContainerRef={diffContainerRef}
        overlayPositions={overlayPositions}
        tone={tone}
        containerSx={containerSx}
        sectionData={sectionData}
        comments={comments}
        currentUser={currentUser}
        onReply={onReply}
        onAcceptChanges={onAcceptChanges}
        renderActions={renderActions}
      />
    </>
  );
};

export default ContentDiffComparison;
